import React, { useState, useEffect, useCallback } from 'react';

import { useLegendLayerInfo, useLegendLayerUpdating } from '../hooks';
import { useViewScale } from '../../map/hooks';

interface LegendSubItemData {
  contentType: string;
  height: number;
  width: number;
  imageData: string;
  label: string;
  url: string;
  groupId: string;
}

interface LegendItemData {
  layerId: number;
  layerName: string;
  minScale: number;
  maxScale: number;
  legend: LegendSubItemData[];
}

interface LegendItemProps {
  data: LegendItemData;
}

const LegendItem: React.FC<LegendItemProps> = ({ data }) => {
  const { layerId, layerName, minScale, maxScale, legend } = data
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const [expanded, setExpanded] = useState<boolean>(false)
  const sublayer = useLegendLayerInfo(layerId)
  const currentScale = useViewScale()

  const disabled = (minScale !== 0 && minScale < currentScale) || (maxScale !== 0 && maxScale > currentScale);

  const [active, setActive] = useState<boolean>(!!sublayer?.visible)
  useEffect(() => {
    setActive(!!sublayer?.visible)
  }, [sublayer])
  const toggle = useCallback(() => {
    if (disabled) {
      setShowTooltip(true)
      setTimeout(() => {
        setShowTooltip(false)
      }, 2000)
      return
    }
    if (sublayer) {
      sublayer.visible = !active
      setActive(!active)
    }
  }, [active, sublayer, setActive, disabled, setShowTooltip])

  const expand = useCallback((isExpanded: boolean) => (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setExpanded(isExpanded)
  }, [])

  const updating = useLegendLayerUpdating()


  if (!data || !sublayer) return null

  const classNames = `row${active ? " on" : ""}${disabled ? " disabled" : ""}`
  const updateIcon = updating && active ? <i className='fa fa-spinner fa-spin pull-right'></i> : ""
  let tooltip = (<div className={`tooltip${showTooltip ? " show" : ""}`}>Zoom closer for this layer.</div>)
  const getSymbol = (imgData: string, name: string) => (<img src={`data:image/png;base64,${imgData}`} alt={name} />)
  if (legend && legend.length > 1) {
    const subItems = legend.map((item, index) => (
      <li key={index}>{getSymbol(item.imageData, item.label)}{item.label}</li>
    ));
    const subItemListClasses = `sub-row${expanded ? " show" : ""}`
    const expansionButtonClasses = `cell cell-condensed ${expanded ? "expanded" : "expand"}`;
    const subItemList = (
      <div className={subItemListClasses}>
        <ul>{subItems}</ul>
      </div>
    );
    const expansionButton = (
      <div className={expansionButtonClasses} onClick={expand(!expanded)}></div>
    );
    return (
      <div className={classNames} onClick={toggle}>
        {!disabled && tooltip}
        <div className="cell cell-condensed content">{updateIcon} {layerName}</div>
        {subItems.length && expansionButton}
        {subItems.length && subItemList}
      </div>
    );
  } else if (legend && legend.length === 1) {
    return (
      <div className={classNames} onClick={toggle}>
        {!disabled && tooltip}
        <div className="cell cell-condensed content">{updateIcon} {layerName}</div>
        <div className="cell cell-condensed symbol">{getSymbol(legend[0].imageData, layerName)}</div>
      </div>
    );
  }
  return null
}

export default LegendItem